import { useEffect, useState } from "react";
import { api, API } from "@/lib/apiClient";
import { AdminLayout } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
  LineChart, Line,
} from "recharts";
import { Download, Trophy, Loader2, Users, Building2, MapPin, HeartHandshake } from "lucide-react";

const MEDALS = ["🥇", "🥈", "🥉"];

function Card({ title, children, action }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-display font-bold text-slate-900">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center gap-4">
      <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#0056D2]/10 text-[#0056D2] shrink-0">
        <Icon className="w-6 h-6" />
      </span>
      <div className="min-w-0">
        <p className="font-display font-extrabold text-2xl leading-none text-slate-900">{value}</p>
        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mt-1">{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// Recognition & performance reports built from record pledges (team
// self-pledges excluded, matching the official numbers).
export default function AdminReportsPage() {
  const [r, setR] = useState(null);
  const [showAllMrs, setShowAllMrs] = useState(false);

  useEffect(() => {
    api.get("/admin/reports").then((res) => setR(res.data)).catch(() => setR(false));
  }, []);

  const exportReport = () => {
    const token = localStorage.getItem("hip_admin_token");
    fetch(`${API}/admin/reports/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "healthy-india-pledge-reports.xlsx";
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Report exported");
      })
      .catch(() => toast.error("Export failed"));
  };

  if (r === null) {
    return (
      <AdminLayout>
        <p className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin inline text-slate-400" /></p>
      </AdminLayout>
    );
  }
  if (r === false) {
    return <AdminLayout><p className="text-center py-20 text-slate-400">Could not load reports.</p></AdminLayout>;
  }

  const mrRows = showAllMrs ? r.top_mrs : r.top_mrs.slice(0, 10);
  const topDivision = r.divisions[0];
  const topHq = r.hqs[0];

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display font-extrabold text-2xl text-slate-900">Reports & Recognition</h2>
        <Button data-testid="export-reports-btn" onClick={exportReport} variant="outline" className="rounded-full border-slate-300">
          <Download className="w-4 h-4 mr-2" /> Download full report (Excel)
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="report-stat-cards">
        <StatCard icon={Users} label="MRs with pledges" value={`${r.participation.mrs_with_pledges.toLocaleString("en-IN")}`}
          sub={`of ${r.participation.total_mrs.toLocaleString("en-IN")} MRs`} />
        <StatCard icon={Building2} label="Top division" value={topDivision ? topDivision.name : "—"}
          sub={topDivision ? `${topDivision.count.toLocaleString("en-IN")} pledges` : ""} />
        <StatCard icon={MapPin} label="Top region (HQ)" value={topHq ? topHq.name : "—"}
          sub={topHq ? `${topHq.count.toLocaleString("en-IN")} pledges` : ""} />
        <StatCard icon={HeartHandshake} label="Team self pledges" value={r.participation.self_pledged.toLocaleString("en-IN")} />
      </div>

      {/* MR leaderboard */}
      <Card
        title={<span className="flex items-center gap-2"><Trophy className="w-5 h-5 text-[#FF9933]" /> MR Leaderboard — highest pledges</span>}
        action={
          <Button data-testid="toggle-leaderboard" onClick={() => setShowAllMrs((v) => !v)} size="sm" variant="ghost" className="rounded-full text-[#0056D2]">
            {showAllMrs ? "Show top 10" : "Show top 50"}
          </Button>
        }
      >
        <div className="overflow-x-auto">
          <Table data-testid="mr-leaderboard">
            <TableHeader>
              <TableRow>
                <TableHead className="w-14">Rank</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Emp Code</TableHead>
                <TableHead>Division</TableHead>
                <TableHead>HQ</TableHead>
                <TableHead>Designation</TableHead>
                <TableHead className="text-right">Pledges</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mrRows.map((m, i) => (
                <TableRow key={m.emp_code} className={i < 3 ? "bg-orange-50/60" : undefined}>
                  <TableCell className="font-display font-extrabold text-lg">{MEDALS[i] || i + 1}</TableCell>
                  <TableCell className="font-medium text-slate-900">{m.name}</TableCell>
                  <TableCell className="text-slate-600">{m.emp_code}</TableCell>
                  <TableCell><Badge variant="outline" className="border-blue-200 bg-blue-50 text-[#0056D2]">{m.division}</Badge></TableCell>
                  <TableCell className="text-slate-600">{m.hq}</TableCell>
                  <TableCell className="text-slate-600 text-xs">{m.designation}</TableCell>
                  <TableCell className="text-right font-display font-extrabold text-slate-900">{m.count.toLocaleString("en-IN")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Pledges by Division">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={r.divisions}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip />
              <Bar dataKey="count" fill="#0056D2" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Top Regions (HQ)">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={r.hqs.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-25} textAnchor="end" height={60} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip />
              <Bar dataKey="count" fill="#FF9933" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title="Day-wise Pledges">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={r.daily}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
            <Tooltip />
            <Line type="monotone" dataKey="count" stroke="#138808" strokeWidth={2.5} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {[["Top States", r.states], ["Top Cities", r.cities], ["Top Specialties", r.specialties]].map(([title, rows]) => (
          <Card key={title} title={title}>
            <div className="space-y-2">
              {rows.slice(0, 10).map((row, i) => {
                const max = rows[0]?.count || 1;
                return (
                  <div key={row.name} className="flex items-center gap-2 text-sm">
                    <span className="w-5 text-slate-400 text-xs">{i + 1}.</span>
                    <span className="w-32 truncate text-slate-700 font-medium">{row.name}</span>
                    <span className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <span className="block h-full rounded-full bg-[#0056D2]" style={{ width: `${(row.count / max) * 100}%` }} />
                    </span>
                    <span className="w-14 text-right font-bold text-slate-800">{row.count.toLocaleString("en-IN")}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      {r.links.length > 0 && (
        <Card title="Campaign / Promotional Links">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Link</TableHead>
                  <TableHead className="text-right">Pledges</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {r.links.map((l) => (
                  <TableRow key={l.name}>
                    <TableCell className="font-medium text-slate-900">/pledge/{l.name}</TableCell>
                    <TableCell className="text-right font-display font-bold">{l.count.toLocaleString("en-IN")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </AdminLayout>
  );
}
